// Sample data for local/preview mode. Clearly fake: bracketed names, sample product ids.
// Real mode starts empty apart from the seeded staples Michael listed.

import type { AddEvent, InboundEmail, ListItem, Order, Segment, SegmentChange, Staple, StoreProduct, Trip } from '@shared/types.ts';
import type { AppState } from './state.ts';
import { emptyState } from './state.ts';

/** The four items Michael asked to seed "Often added" with, in his order. */
export const SEED_STAPLES: Staple[] = [
  { id: 'st_strawberries', name: 'Strawberries', usualQty: 1, usual: true, seedRank: 0 },
  { id: 'st_blueberries', name: 'Blueberries', usualQty: 1, usual: true, seedRank: 1 },
  { id: 'st_greek_yogurt', name: 'Greek yogurt', usualQty: 2, usual: true, seedRank: 2 },
  { id: 'st_granola', name: 'Granola', usualQty: 1, usual: true, seedRank: 3 },
];

const SAMPLE_STAPLES: Staple[] = [
  { id: 'st_oat_milk', name: 'Oat milk', usualQty: 2, usual: false },
  { id: 'st_bananas', name: 'Bananas', usualQty: 6, usual: false },
  { id: 'st_eggs', name: 'Eggs, dozen', usualQty: 1, usual: false },
  { id: 'st_spinach', name: 'Baby spinach', usualQty: 1, usual: false },
  { id: 'st_paper_towels', name: 'Paper towels', usualQty: 1, usual: false },
  { id: 'st_chicken', name: 'Chicken thighs', usualQty: 1, usual: false },
  { id: 'st_olive_oil', name: 'Olive oil', usualQty: 1, usual: false },
  { id: 'st_coffee', name: 'Coffee beans', usualQty: 1, usual: false },
  { id: 'st_avocados', name: 'Avocados', usualQty: 4, usual: false },
];

function sp(stapleId: string, storeId: StoreProduct['storeId'], productId: string, title: string, lastPrice: number, sizeLabel?: string, unitAmount?: number, unit?: string): StoreProduct {
  return {
    id: `sp_${storeId}_${stapleId}`,
    stapleId,
    storeId,
    productId,
    title,
    lastPrice,
    sizeLabel,
    unitAmount,
    unit,
    available: true,
    updatedAt: '2026-10-12T12:00:00.000Z',
  };
}

const SAMPLE_PRODUCTS: StoreProduct[] = [
  sp('st_strawberries', 'amazonfresh', 'B0SAMPLE01', 'Strawberries, 1 lb (sample)', 4.49, '1 lb', 16, 'oz'),
  sp('st_strawberries', 'costco', '100001', 'Strawberries, 2 lb (sample)', 7.99, '2 lb', 32, 'oz'),
  sp('st_blueberries', 'amazonfresh', 'B0SAMPLE02', 'Blueberries, 6 oz (sample)', 3.99, '6 oz', 6, 'oz'),
  sp('st_blueberries', 'costco', '100002', 'Blueberries, 18 oz (sample)', 8.49, '18 oz', 18, 'oz'),
  sp('st_greek_yogurt', 'amazonfresh', 'B0SAMPLE03', 'Greek yogurt, 32 oz (sample)', 5.99, '32 oz', 32, 'oz'),
  sp('st_granola', 'amazon', 'B0SAMPLE04', 'Granola, 12 oz (sample)', 5.49, '12 oz', 12, 'oz'),
  sp('st_oat_milk', 'amazonfresh', 'B0SAMPLE05', 'Oat milk, 64 fl oz (sample)', 4.49),
  sp('st_bananas', 'amazonfresh', 'B0SAMPLE06', 'Banana, each (sample)', 0.29),
  sp('st_eggs', 'amazonfresh', 'B0SAMPLE07', 'Eggs, dozen (sample)', 5.29),
  sp('st_spinach', 'amazonfresh', 'B0SAMPLE08', 'Baby spinach, 5 oz (sample)', 3.99),
  sp('st_paper_towels', 'amazon', 'B0SAMPLE09', 'Paper towels, 6 rolls (sample)', 21.99),
  sp('st_chicken', 'costco', '100003', 'Chicken thighs, ~4 lb (sample)', 13.99),
  sp('st_olive_oil', 'costco', '100004', 'Olive oil, 2 L (sample)', 17.99, '2 L', 2000, 'ml'),
  sp('st_olive_oil', 'amazon', 'B0SAMPLE10', 'Olive oil, 750 ml (sample)', 12.99, '750 ml', 750, 'ml'),
  sp('st_coffee', 'amazon', 'B0SAMPLE11', 'Coffee beans, 2 lb (sample)', 16.99),
];

function li(id: string, name: string, qty: number, addedBy: 'michael' | 'natasha', stapleId?: string, minutesAgo = 30): ListItem {
  const at = new Date(Date.now() - minutesAgo * 60_000).toISOString();
  return { id, name, qty, addedBy, addedAt: at, updatedAt: at, status: 'needed', stapleId };
}

function sampleEvents(): AddEvent[] {
  const day = 86_400_000;
  const at = (d: number) => new Date(Date.now() - d * day).toISOString();
  return [
    { name: 'Coffee beans', stapleId: 'st_coffee', at: at(44), by: 'michael' },
    { name: 'Coffee beans', stapleId: 'st_coffee', at: at(30), by: 'natasha' },
    { name: 'Coffee beans', stapleId: 'st_coffee', at: at(15), by: 'michael' },
    { name: 'Avocados', stapleId: 'st_avocados', at: at(9), by: 'natasha' },
    { name: 'Avocados', stapleId: 'st_avocados', at: at(3), by: 'natasha' },
    { name: 'Lemons', at: at(20), by: 'michael' },
  ];
}

function nowMinus(minutes: number): string {
  return new Date(Date.now() - minutes * 60_000).toISOString();
}

const TRIPS: Trip[] = [
  { id: 'trip_lisbon', title: 'Lisbon', destination: 'Lisbon', startDate: '2026-11-03', endDate: '2026-11-09' },
  { id: 'trip_chicago', title: 'Chicago', destination: 'Chicago', startDate: '2026-12-18', endDate: '2026-12-21' },
  { id: 'trip_montreal', title: 'Montréal', destination: 'Montréal', startDate: '2026-08-22', endDate: '2026-08-25' },
];

const T = '2026-10-02T14:00:00.000Z';
function seg(s: Omit<Segment, 'status' | 'details' | 'source' | 'createdAt' | 'updatedAt' | 'travelers'> & Partial<Segment>): Segment {
  return {
    status: 'active',
    details: {},
    travelers: ['Michael Tan', '[Natalie]'],
    source: { parsedBy: 'jsonld', emailId: 'em_sample_tap' },
    createdAt: T,
    updatedAt: T,
    ...s,
  };
}

const SEGMENTS: Segment[] = [
  seg({
    id: 'seg_tp202',
    tripId: 'trip_lisbon',
    type: 'flight',
    title: 'JFK → LIS',
    provider: 'TAP Air Portugal',
    number: 'TP 202',
    confirmation: '[CODE]',
    start: { date: '2026-11-03', time: '19:40', timeZone: 'America/New_York' },
    end: { date: '2026-11-04', time: '07:55', timeZone: 'Europe/Lisbon' },
    from: { code: 'JFK', city: 'New York', terminal: '1', timeZone: 'America/New_York' },
    to: { code: 'LIS', city: 'Lisbon', terminal: '1', timeZone: 'Europe/Lisbon' },
    seats: ['23A', '23B'],
  }),
  seg({
    id: 'seg_hotel',
    tripId: 'trip_lisbon',
    type: 'stay',
    title: '[Hotel]',
    confirmation: '[CODE]',
    start: { date: '2026-11-04', time: '15:00', timeZone: 'Europe/Lisbon' },
    end: { date: '2026-11-09', time: '11:00', timeZone: 'Europe/Lisbon' },
    place: { name: '[Hotel]', city: 'Lisbon', address: 'Alfama, Lisbon' },
    source: { parsedBy: 'ai', emailId: 'em_sample_hotel' },
  }),
  seg({
    id: 'seg_dinner',
    tripId: 'trip_lisbon',
    type: 'reservation',
    title: '[Restaurant]',
    start: { date: '2026-11-04', time: '20:30', timeZone: 'Europe/Lisbon' },
    place: { name: '[Restaurant]', city: 'Lisbon' },
    details: { 'Party size': '2' },
    source: { parsedBy: 'ai' },
  }),
  seg({
    id: 'seg_sintra',
    tripId: 'trip_lisbon',
    type: 'activity',
    title: 'Day trip · Sintra',
    start: { date: '2026-11-07', time: '09:00', timeZone: 'Europe/Lisbon' },
    place: { name: 'Sintra', city: 'Sintra' },
    details: { Tickets: '2' },
    source: { parsedBy: 'ai' },
  }),
  seg({
    id: 'seg_tp203',
    tripId: 'trip_lisbon',
    type: 'flight',
    title: 'LIS → JFK',
    provider: 'TAP Air Portugal',
    number: 'TP 203',
    confirmation: '[CODE]',
    start: { date: '2026-11-09', time: '13:15', timeZone: 'Europe/Lisbon' },
    end: { date: '2026-11-09', time: '16:30', timeZone: 'America/New_York' },
    from: { code: 'LIS', city: 'Lisbon', timeZone: 'Europe/Lisbon' },
    to: { code: 'JFK', city: 'New York', timeZone: 'America/New_York' },
  }),
  seg({
    id: 'seg_ord_out',
    tripId: 'trip_chicago',
    type: 'flight',
    title: 'LGA → ORD',
    number: '[Flight]',
    confirmation: '[CODE]',
    start: { date: '2026-12-18', time: '08:00', timeZone: 'America/New_York' },
    end: { date: '2026-12-18', time: '09:45', timeZone: 'America/Chicago' },
    from: { code: 'LGA', city: 'New York', timeZone: 'America/New_York' },
    to: { code: 'ORD', city: 'Chicago', timeZone: 'America/Chicago' },
  }),
  seg({
    id: 'seg_ord_stay',
    tripId: 'trip_chicago',
    type: 'stay',
    title: '[Hotel]',
    start: { date: '2026-12-18', time: '15:00', timeZone: 'America/Chicago' },
    end: { date: '2026-12-21', time: '11:00', timeZone: 'America/Chicago' },
    place: { name: '[Hotel]', city: 'Chicago' },
  }),
  seg({
    id: 'seg_ord_back',
    tripId: 'trip_chicago',
    type: 'flight',
    title: 'ORD → LGA',
    number: '[Flight]',
    confirmation: '[CODE]',
    start: { date: '2026-12-21', time: '17:00', timeZone: 'America/Chicago' },
    end: { date: '2026-12-21', time: '20:10', timeZone: 'America/New_York' },
    from: { code: 'ORD', city: 'Chicago', timeZone: 'America/Chicago' },
    to: { code: 'LGA', city: 'New York', timeZone: 'America/New_York' },
  }),
  seg({
    id: 'seg_yul_stay',
    tripId: 'trip_montreal',
    type: 'stay',
    title: '[Hotel]',
    start: { date: '2026-08-22', time: '15:00', timeZone: 'America/Toronto' },
    end: { date: '2026-08-25', time: '11:00', timeZone: 'America/Toronto' },
    place: { name: '[Hotel]', city: 'Montréal' },
  }),
];

const CHANGES: SegmentChange[] = [
  { id: 'chg_1', segmentId: 'seg_tp202', at: '2026-10-02T14:00:00.000Z', emailId: 'em_sample_tap', summary: 'Added to trip' },
  { id: 'chg_2', segmentId: 'seg_tp202', at: '2026-10-12T09:00:00.000Z', emailId: 'em_sample_change', summary: 'Departure 7:10 PM → 7:40 PM' },
];

const EMAILS: InboundEmail[] = [
  {
    id: 'em_sample_porto',
    gmailId: 'sample-porto',
    fromAddress: 'michael@example.com',
    forwardedBy: 'michael',
    subject: 'Your stay is confirmed',
    receivedAt: '2026-10-18T16:20:00.000Z',
    kind: 'travel',
    status: 'failed',
    parsedBy: 'ai',
    problems: ['Missing: check-in date'],
    draft: {
      type: 'stay',
      title: '[Guesthouse]',
      start: { date: '' },
      end: { date: '2026-11-12' },
      place: { name: '[Guesthouse]', city: 'Porto' },
      confirmation: '[CODE]',
      travelers: [],
    },
    bodyPreview:
      'From: [booking site]\nSubject: Your stay is confirmed\n\n[Guesthouse], Porto\nYour stay: 3 nights, check-out Thursday, November 12\nConfirmation: [CODE]\n(Sample email: the check-in date is only in an image, so it could not be read.)',
  },
];

function sampleOrders(): Order[] {
  return [
    {
      id: 'ord_sample',
      storeId: 'amazon',
      status: 'placed',
      placedAt: nowMinus(240),
      placedBy: 'natasha',
      estTotal: 18.47,
      lines: [
        { listItemId: 'li_done_1', name: 'Strawberries', qty: 1, estPrice: 4.49 },
        { listItemId: 'li_done_2', name: 'Blueberries', qty: 2, estPrice: 3.99 },
        { listItemId: 'li_done_3', name: 'Granola', qty: 1, estPrice: 5.49 },
      ],
    },
  ];
}

export function sampleState(inboxAddress: string): AppState {
  const base = emptyState(inboxAddress);
  const list: ListItem[] = [
    li('li_oat', 'Oat milk', 2, 'michael', 'st_oat_milk', 3),
    li('li_bananas', 'Bananas', 6, 'natasha', 'st_bananas', 5),
    li('li_eggs', 'Eggs, dozen', 1, 'michael', 'st_eggs', 8),
    li('li_spinach', 'Baby spinach', 1, 'natasha', 'st_spinach', 9),
    li('li_towels', 'Paper towels', 1, 'michael', 'st_paper_towels', 20),
    li('li_yogurt', 'Greek yogurt', 2, 'natasha', 'st_greek_yogurt', 25),
    li('li_chicken', 'Chicken thighs', 1, 'michael', 'st_chicken', 40),
    li('li_oil', 'Olive oil', 1, 'natasha', 'st_olive_oil', 42),
    li('li_soap', 'Dish soap', 1, 'michael', undefined, 50),
  ];
  for (const o of sampleOrders()) {
    for (const l of o.lines) {
      list.push({ id: l.listItemId, name: l.name, qty: l.qty, addedBy: 'natasha', addedAt: o.placedAt, updatedAt: o.placedAt, status: 'ordered', orderId: o.id });
    }
  }
  return {
    ...base,
    me: 'michael',
    list,
    staples: [...SEED_STAPLES, ...SAMPLE_STAPLES],
    products: SAMPLE_PRODUCTS,
    orders: sampleOrders(),
    events: sampleEvents(),
    trips: TRIPS,
    segments: SEGMENTS,
    changes: CHANGES,
    emails: EMAILS,
  };
}

/** A real (non-sample) first run: empty apart from Michael's seeded staples. */
export function initialRealState(inboxAddress: string): AppState {
  return { ...emptyState(inboxAddress), staples: SEED_STAPLES };
}
